<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { ChatMessage } from '@/services/chat/chatHttp.service'
import { chatApi } from '@/services/api'
import { useUserStore } from '@/config/store/userStore'
import { withPollViewerState } from '@/services/chat/pollVisibility'

const { t } = useI18n()
const userStore = useUserStore()

const props = defineProps<{
  message: ChatMessage
  tone?: 'default' | 'on-primary'
}>()

const emits = defineEmits<{
  (e: 'updated', message: ChatMessage): void
}>()

const voting = ref(false)

const poll = computed(() =>
  withPollViewerState(props.message.poll, String(userStore.user?._id || '')),
)
const onPrimary = computed(() => props.tone === 'on-primary')

const visibleOptions = computed(() => {
  const current = poll.value
  if (!current) return []
  if (current.showResults) return current.options
  return current.options.map(option => ({
    ...option,
    voteCount: 0,
    percentage: 0,
  }))
})

const visibleTotalVotes = computed(() => (poll.value?.showResults ? poll.value.totalVotes : 0))

const vote = async (optionId: string) => {
  if (!poll.value || voting.value) return
  if (poll.value.hasVoted) return
  voting.value = true
  try {
    const updated = await chatApi.votePoll(props.message._id, optionId).promise
    emits('updated', updated)
  }
  catch (err) {
    console.error('Failed to vote', err)
  }
  finally {
    voting.value = false
  }
}
</script>

<template>
  <div
    v-if="poll"
    class="mt-1 space-y-2 min-w-[220px]"
    data-cy="chat-poll"
  >
    <div
      class="font-medium text-sm"
      data-cy="chat-poll-question-label"
      :class="onPrimary ? 'text-white' : ''"
    >
      {{ poll.question }}
    </div>

    <div class="space-y-1.5">
      <button
        v-for="option in visibleOptions"
        :key="option._id"
        type="button"
        class="relative w-full text-left rounded-lg overflow-hidden border px-2.5 py-1.5 text-sm transition-colors disabled:cursor-default"
        :class="[
          onPrimary
            ? 'border-white/30 hover:bg-white/10 disabled:hover:bg-transparent'
            : 'border-black/10 dark:border-white/15 bg-hover/40 hover:bg-hover disabled:hover:bg-hover/40',
          poll.myOptionId === option._id
            ? (onPrimary ? 'ring-1 ring-white' : 'ring-1 ring-primary-500')
            : '',
        ]"
        :data-cy="`chat-poll-vote-${option.text}`"
        :disabled="poll.hasVoted || voting"
        @click="vote(option._id)"
      >
        <div
          v-if="poll.showResults"
          class="absolute inset-y-0 left-0 opacity-25 pointer-events-none"
          :class="onPrimary ? 'bg-white' : 'bg-primary-500'"
          :style="{ width: `${option.percentage}%` }"
        />
        <div class="relative flex items-center justify-between gap-2">
          <span class="truncate">{{ option.text }}</span>
          <span
            v-if="poll.showResults"
            class="shrink-0 text-xs opacity-80 tabular-nums"
          >
            {{ option.percentage }}%
          </span>
        </div>
      </button>
    </div>

    <div
      class="text-[11px] opacity-70"
      :class="onPrimary ? 'text-white/80' : ''"
    >
      <template v-if="poll.showResults">
        {{ t('modules.chat.poll.voteCount', { count: visibleTotalVotes }) }}
        <span v-if="poll.isAuthor"> · {{ t('modules.chat.poll.liveResults') }}</span>
      </template>
      <template v-else>
        {{ t('modules.chat.poll.chooseOption') }}
      </template>
    </div>
  </div>
</template>
